import type {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import axios from 'axios';
import {message} from 'antd';
import {store} from '@/store';
import {logout} from '@/store/slices/authSlice';

// 扩展 AxiosInstance 类型，使其知道响应拦截器会返回 data.data
declare module 'axios' {
  export interface AxiosInstance {
    get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;

    post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;

    put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;

    delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;

    patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  }
}

const request: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081',
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data, config } = response;

    // 如果是 blob 类型响应，直接返回数据
    if (config.responseType === 'blob') {
      return data;
    }

    // 如果响应中包含code和message字段，说明是我们的标准响应格式
    if (data && typeof data === 'object' && 'code' in data) {
      if (data.code === 200) {
        return data.data;
      } else {
        // 业务错误
        const error = new Error(data.message || '请求失败');
        error.name = 'BusinessError';
        return Promise.reject(error);
      }
    }
    return data;
  },
  (error) => {
    if (error.response) {
      const { data, status } = error.response;
      // 如果响应中包含我们的标准错误格式
      if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
        // 对于登录相关的错误，不在这里显示消息，让组件自己处理
        if (![1001, 1002, 1005, 1006, 1007, 1008, 1009].includes(data.code)) {
          message.error(data.message || '请求失败');
        }
        return Promise.reject(new Error(data.message));
      }

      // 处理HTTP状态码
      switch (status) {
        case 401:
          store.dispatch(logout());
          message.error('登录已过期，请重新登录');
          break;
        case 403:
          message.error('没有权限访问');
          break;
        case 404:
          message.error('请求的资源不存在');
          break;
        case 500:
          message.error('服务器错误');
          break;
        default:
          message.error(data?.message || '请求失败');
      }
    } else if (error.request) {
      message.error('网络错误，请检查网络连接');
    } else {
      message.error('请求配置错误');
    }
    return Promise.reject(error);
  },
);

export default request;
